import { useState } from 'react';
import { IconButton } from '@mui/material';
import './DbmHistory.scss';
import {
  GridSortModel,
  GridColumnHeaderParams,
  DataGridPro,
  GridPaginationModel,
  GridPagination
} from '@mui/x-data-grid-pro';
import { useEffect } from 'react';
import { useDBM } from '../../providers/DBM';
import { useTranslation } from 'react-i18next';
import { DBMDataInterface } from '../../models/DBMDataInterface';
import {
  getNumDigits,
  initializeState,
  showAllRows
} from '../../helpers/viewAllHelper';

export default function DbmHistory({ to }) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState<DBMDataInterface>({ dbmlist: [] });
  // const [pageSize, setPageSize] = useState(25);
  const yearEnd = parseInt(to);
  const dbmContext = useDBM();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [xGridColumns, setXGridColumns] = useState<any>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const [viewAllClicked, setViewAllClicked] = useState(false);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState([25, 50, 100]);

  useEffect(() => {
    if (!dbmContext?.dbmYearlyData?.dbmlist) {
      return;
    }
    setTableData(dbmContext?.dbmYearlyData);
    setXGridColumns(columns);
    setSortModel([
      {
        field: 'equipment',
        sort: 'asc'
      }
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbmContext?.dbmYearlyData.dbmlist, dbmContext?.dbmYearlyData]);

  useEffect(() => {
    initializeState(
      paginationModel,
      setPaginationModel,
      setRowsPerPageOptions,
      viewAllClicked,
      tableData.dbmlist.length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.dbmlist, viewAllClicked]);

  const CustomNoRowsOverlay = () => {
    return (
      <div className="no-rows">
        <i className="icon-error-warning" />
        <div>{t('dbmHistoryNoData')}</div>
      </div>
    );
  };

  const columns = [
    {
      field: '',
      width: 45,
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <div style={{ cursor: 'pointer' }}>
          <IconButton aria-label="my pairing" size="large">
            {params.row.preferred === true && (
              <span className="icon-profile"></span>
            )}
          </IconButton>
        </div>
      )
    },
    { field: 'baseAirport', headerName: 'Base', width: 55, editable: false },
    { field: 'position', headerName: 'Position', width: 85, editable: false },
    {
      field: 'equipment',
      headerName: 'Equipment',
      width: 120,
      editable: false
    },
    {
      field: 'jun',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">JUN {yearEnd - 1}</span>;
      },
      width: 70,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[0]);
      }
    },
    {
      field: 'jul',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">JUL {yearEnd - 1}</span>;
      },
      width: 65,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[1]);
      }
    },
    {
      field: 'aug',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">AUG {yearEnd - 1}</span>;
      },
      width: 71,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[2]);
      }
    },
    {
      field: 'sep',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">SEP {yearEnd - 1}</span>;
      },
      width: 65,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[3]);
      }
    },
    {
      field: 'oct',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">OCT {yearEnd - 1}</span>;
      },
      width: 70,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[4]);
      }
    },
    {
      field: 'nov',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">NOV {yearEnd - 1}</span>;
      },
      width: 75,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[5]);
      }
    },
    {
      field: 'dec',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">DEC {yearEnd - 1}</span>;
      },
      width: 69,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[6]);
      }
    },
    {
      field: 'jan',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">JAN {yearEnd}</span>;
      },
      width: 66,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[7]);
      }
    },
    {
      field: 'feb',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">FEB {yearEnd}</span>;
      },
      width: 70,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[8]);
      }
    },
    {
      field: 'mar',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">MAR {yearEnd}</span>;
      },
      width: 73,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[9]);
      }
    },
    {
      field: 'apr',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">APR {yearEnd}</span>;
      },
      width: 68,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[10]);
      }
    },
    {
      field: 'may',
      renderHeader: (params: GridColumnHeaderParams) => {
        return <span className="column-title">MAY {yearEnd}</span>;
      },
      width: 73,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return Object.values(params.row.dbm[11]);
      }
    },
    { field: 'sum', headerName: 'SUM', width: 60, editable: false },
    { field: 'avg', headerName: 'AVG', width: 60, editable: false }
  ];
  // TODO integration: getRowClassName needs connection
  return (
    <>
      <div className="dbm-report-table">
        <DataGridPro
          autoHeight
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            pagination: (props: any) => (
              <>
                <div
                  className={`view-all-${getNumDigits(paginationModel.pageSize)}`}
                >
                  {paginationModel.pageSize < tableData.dbmlist.length ? (
                    <div className="enabled-view-all">
                      <span
                        onClick={() =>
                          showAllRows(
                            setViewAllClicked,
                            tableData.dbmlist.length,
                            paginationModel,
                            setPaginationModel
                          )
                        }
                      >
                        View All
                      </span>
                    </div>
                  ) : tableData.dbmlist.length > 10 ? (
                    <div className="disabled-view-all">
                      <span>View All</span>
                    </div>
                  ) : (
                    <div className="disabled-view-all-adjusted">
                      <span>View All</span>
                    </div>
                  )}
                </div>
                <GridPagination />
              </>
            )
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          //disableSelectionOnClick
          //disableExtendRowFullWidth={true}
          // pageSize={pageSize}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={rowsPerPageOptions}
          // onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          pagination
          getRowId={(r) => r.id}
          rows={tableData.dbmlist}
          columns={xGridColumns}
          getRowClassName={(params) =>
            params?.row?.preferred === true ? 'profile-row' : ''
          }
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
        />
      </div>
    </>
  );
}
